import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import clientSiteService from '../../../services/clientSiteService';

const AddClientSiteModal = ({ isOpen, onClose, site, clients, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    site_name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    latitude: '',
    longitude: '',
    contact_person: '',
    phone: '',
    email: '',
    is_active: true
  });

  useEffect(() => {
    if (site) {
      setFormData({
        client_id: site.client_id || '',
        site_name: site.site_name || '',
        address: site.address || '',
        city: site.city || '',
        state: site.state || '',
        pincode: site.pincode || '',
        country: site.country || 'India',
        latitude: site.latitude?.toString() || '',
        longitude: site.longitude?.toString() || '',
        contact_person: site.contact_person || '',
        phone: site.phone || '',
        email: site.email || '',
        is_active: site.is_active !== undefined ? site.is_active : true
      });
    } else {
      setFormData({
        client_id: '',
        site_name: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        latitude: '',
        longitude: '',
        contact_person: '',
        phone: '',
        email: '',
        is_active: true
      });
    }
    setError(null);
  }, [site, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        }));
      },
      (error) => {
        setError('Failed to get location: ' + error.message);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      setError('Client is required');
      return;
    }

    if (!formData.site_name.trim()) {
      setError('Site name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        is_active: formData.is_active
      };

      const result = site
        ? await clientSiteService.updateClientSite(site.id, submitData)
        : await clientSiteService.createClientSite(submitData);

      if (result?.success) {
        onSave?.();
      } else {
        setError(result?.error || `Failed to ${site ? 'update' : 'create'} client site`);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Client site save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">
            {site ? 'Edit Client Site' : 'Add New Client Site'}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Client *
              </label>
              <select
                value={formData.client_id}
                onChange={(e) => handleInputChange('client_id', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                required
                disabled={!!site} // Don't allow changing client for existing site
              >
                <option value="">Select Client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.company_name}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <Input
                label="Site Name *"
                value={formData.site_name}
                onChange={(e) => handleInputChange('site_name', e.target.value)}
                required
                placeholder="e.g., Main Office, Warehouse, Branch Office"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                rows="3"
                placeholder="Full address"
              />
            </div>

            <Input
              label="City"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="City"
            />

            <Input
              label="State"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="State"
            />

            <Input
              label="PIN Code"
              value={formData.pincode}
              onChange={(e) => handleInputChange('pincode', e.target.value)}
              placeholder="PIN Code"
            />

            <Input
              label="Country"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              placeholder="Country"
            />

            <div className="md:col-span-2 border-t border-border pt-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Location Coordinates (for Geo-fencing)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex gap-2">
                    <Input
                      label="Latitude"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => handleInputChange('latitude', e.target.value)}
                      placeholder="28.6139"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={getCurrentLocation}
                      className="mt-6"
                      title="Use current location"
                    >
                      <Icon name="MapPin" size={16} />
                    </Button>
                  </div>
                </div>
                <Input
                  label="Longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                  placeholder="77.2090"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Coordinates are used for geo-fencing and attendance validation
              </p>
            </div>

            <div className="md:col-span-2 border-t border-border pt-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Person"
                  value={formData.contact_person}
                  onChange={(e) => handleInputChange('contact_person', e.target.value)}
                  placeholder="Site contact person"
                />

                <Input
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+91 1234567890"
                />

                <div className="md:col-span-2">
                  <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="site@example.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleInputChange('is_active', e.target.value === 'active')}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  {site ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                site ? 'Update Site' : 'Create Site'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientSiteModal;

